"use client";

import type React from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useWindowSize } from "@/hooks/use-window";
import { useSidebar } from "./sidebar-context";
import { TsOriginLogo } from "@/components/brand/ts-origin-logo";
import { TrungThuHeaderDecor } from "@/components/layout/trung-thu-header-decor";
import { WomensDayHeaderDecor } from "@/components/layout/womens-day-header-decor";
import { TeachersDayHeaderDecor } from "@/components/layout/teachers-day-header-decor";
import { isTrungThuSeason } from "@/lib/trung-thu-season";
import { isWomensDaySeason } from "@/lib/womens-day-season";
import { isTeachersDaySeason } from "@/lib/teachers-day-season";
import {
  Award,
  FileQuestion,
  LogOut,
  Medal,
  Monitor,
  Newspaper,
  ScrollText,
  ShoppingBag,
  Store,
  Swords,
  Trophy,
  Users,
  X,
  Wrench,
  CalendarDays,
  Crown,
  ListChecks,
  UserPlus,
  ShieldCheck,
  BookText,
  Wallet, 
  MessageCircleQuestion,
  Server,
  MessageCircle,
  Sparkles,
  Zap,
} from "lucide-react";

export function AppSidebar() {
  const pathname = usePathname();
  const { collapsed, setCollapsed } = useSidebar();

  const getRootSegment = (path: string) => {
    const noHash = String(path || "").split("#")[0] || "/";
    const noQuery = noHash.split("?")[0] || "/";
    const trimmed = noQuery.replace(/\/+$/, "/");
    const parts = trimmed.split("/").filter(Boolean);
    return parts[0] || "";
  };

  const currentRoot = getRootSegment(pathname || "/");
  const trungThu = isTrungThuSeason();
  const teachersDay = !trungThu && isTeachersDaySeason();
  const womensDay = !trungThu && !teachersDay && isWomensDaySeason();
  const festiveTheme = trungThu
    ? ("trung-thu" as const)
    : teachersDay
      ? ("teachers-day" as const)
      : womensDay
        ? ("womens-day" as const)
        : null;
  const isActive = (path: string) => {
    const targetRoot = getRootSegment(path);
    return currentRoot === targetRoot;
  };

  const menuItems: Array<{
    label: string;
    icon: React.ReactNode;
    tone?: "indigo" | "emerald" | "amber";
    href?: string;
    children?: Array<{
      label: string;
      href: string;
      icon: React.ReactNode;
      badge?: string;
      iconHighlight?: IconHighlight;
      /** Card sự kiện nổi bật (ảnh + tiêu đề). */
      featured?: boolean;
      featuredImage?: string;
      featuredHint?: string;
      /** Trang redirect sang coming-soon — không highlight active trên menu. */
      comingSoon?: boolean;
    }>;
  }> = [
    { label: "Tải game", href: "/download", icon: <Swords className="size-5" /> },
    { label: "Giả lập máy ảo", href: "/virtual-machine", icon: <Monitor className="size-5" /> },
    { label: "Tool hỗ trợ game", href: "/tool", icon: <Wrench className="size-5" /> },
    { label: "Discord", href: "https://discord.gg/rVUnKXu8mY", icon: <MessageCircle className="size-5" /> },
    { label: "Xếp hạng", href: "/leaderboard", icon: <Medal className="size-5" /> },
    {
      label: "Chức năng",
      icon: <ListChecks className="size-5" />,
      tone: "emerald",
      children: [
        { label: "Nạp point", href: "/pricing", icon: <Wallet className="size-5" /> },
        { label: "Tạo account game", href: "/account", icon: <UserPlus className="size-5" /> },
        { label: "Danh sách tài khoản", href: "/list-account", icon: <Users className="size-5" /> },
        { label: "Máy chủ VPS", href: "/vps", icon: <Server className="size-5" /> },
        { label: "Cửa hàng", href: "/limited-store", icon: <Store className="size-5" /> },
        { label: "Chợ phiên", href: "/cho-phien", icon: <ShoppingBag className="size-5" /> },
      ],
    },
    {
      label: "Sự kiện",
      icon: <CalendarDays className="size-5" />,
      tone: "amber",
      children: [
        {
          label: "Quần Hùng Tranh Bá",
          href: "/heroes-contest",
          icon: <Crown className="size-5 fill-amber-400/20 text-amber-400" />,
          badge: "Đợt 2",
          iconHighlight: "gold",
          featured: true,
          featuredImage: "/images/hero-contest/ti-cau.png",
          featuredHint: "Công bố Top 3",
        },
        { label: "Mốc nạp nhận quà", href: "/deposit-reward", icon: <Award className="size-5" /> },
        // { label: "Điểm danh", href: "/daily-challenge", icon: <ShieldCheck className="size-5" /> },
        // { label: "Ruộng kỳ trân", href: "#", icon: <Trophy className="size-5" /> },
        // { label: "Hành trình tích lũy", href: "#", icon: <Award className="size-5" /> },
        // { label: "Gói quân lương", href: "#", icon: <DollarSign className="size-5" /> },
        // { label: "Quân đoàn đồng hành", href: "#", icon: <Users className="size-5" /> },
        { label: "Điểm danh", href: "/daily-checkin", icon: <ShieldCheck className="size-5" /> },
        {
          label: "Hành trình lịch sử",
          href: "/historical-journey",
          icon: <BookText className="size-5 fill-amber-400/15 text-amber-300" />,
          badge: "Mới",
          iconHighlight: "gold",
          featured: true,
          featuredImage: "/images/historical-journey/chapter-1/main.jpeg",
          featuredHint: "Ghép mảnh · Tam Quốc",
        },
        {
          label: "Thẻ Bài May Mắn",
          href: "/lucky-card",
          icon: <Sparkles className="size-5" />,
        },
        // Tạm ẩn Pet Cards lab
        // {
        //   label: "Pet Cards",
        //   href: "/pet-cards",
        //   icon: <Sparkles className="size-5" />,
        //   badge: "Lab",
        // },
        {
          label: "Rương kỳ trân",
          href: "/treasure-chest",
          icon: <Trophy className="size-5" />,
          badge: "20/11",
        },
        {
          label: "Loạn thế hùng nhân",
          href: "/chaos-heroes",
          icon: <Swords className="size-5 fill-rose-500/20 text-rose-400" />,
          iconHighlight: "crimson",
        },
        {
          label: "Thiên hạ hùng chủ",
          href: "/world-lord",
          icon: <Crown className="size-5 fill-amber-400/25 text-amber-400" />,
          iconHighlight: "gold",
        },
        // Tạm ẩn — bật lại khi sự kiện WC mở
        // {
        //   label: "Battle Origin",
        //   href: "/explore",
        //   icon: <Gamepad2 className="size-5" />,
        //   badge: "WC 2026",
        // },
      ],
    },
    {
      label: "Bài viết",
      icon: <BookText className="size-5" />,
      tone: "indigo",
      children: [
        { label: "Hướng dẫn", href: "/news", icon: <FileQuestion className="size-5" /> },
        { label: "Tính năng", href: "/creator-tips", icon: <Newspaper className="size-5" /> },
        { label: "Sự kiện", href: "/events", icon: <ScrollText className="size-5" /> },
        { label: "Hỏi đáp thường gặp", href: "/support", icon: <MessageCircleQuestion className="size-5" /> },
      ],
    },
  ];

  return (
    <aside
      className={cn(
        "max-xl:fixed max-xl:top-0 max-xl:left-0 max-xl:h-full xl:sticky top-0 flex h-screen flex-col overflow-x-hidden border-r bg-background transition-all duration-300",
        collapsed ? "w-[250px] xl:w-[70px]" : "w-[250px]",
        collapsed ? "max-xl:-translate-x-full" : "max-xl:translate-x-0"
      )}
    >
      <div
        className={cn(
          "relative flex h-16 items-center max-xl:justify-between gap-2 border-b px-4 overflow-hidden",
          collapsed ? "justify-center" : "justify-between",
          trungThu
            ? "border-amber-500/25 bg-gradient-to-br from-amber-950/45 via-background to-rose-950/30"
            : teachersDay
              ? "border-emerald-500/30 bg-gradient-to-br from-emerald-950/50 via-background to-rose-950/30"
              : womensDay
                ? "border-rose-500/30 bg-gradient-to-br from-rose-950/50 via-background to-pink-950/35"
                : ""
        )}
      >
        {trungThu && <TrungThuHeaderDecor />}
        {teachersDay && <TeachersDayHeaderDecor />}
        {womensDay && <WomensDayHeaderDecor />}
        <Link href="/" className="relative z-10 flex items-center justify-center gap-2">
          <TsOriginLogo
            showText={!collapsed}
            size={collapsed ? "sm" : "md"}
            festiveTheme={festiveTheme}
          />
        </Link>

        <button className="relative z-10 xl:hidden" onClick={() => setCollapsed(!collapsed)}>
          <X className="size-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4">
        <nav className="grid min-w-0 gap-1 px-2">
          {menuItems.map((item) =>
            item.children && item.children.length > 0 ? (
              <div
                key={item.label}
                className={cn(
                  "grid gap-1 rounded-xl border p-1.5",
                  item.tone === "emerald" && "border-emerald-500/20 bg-emerald-500/[0.05]",
                  item.tone === "amber" &&
                    (womensDay
                      ? "border-rose-500/25 bg-rose-500/[0.06]"
                      : "border-amber-500/20 bg-amber-500/[0.05]"),
                  item.tone !== "emerald" && item.tone !== "amber" && "border-indigo-500/20 bg-indigo-500/[0.05]"
                )}
              >
                {!collapsed && (
                  <div className="flex items-center gap-2 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        item.tone === "emerald" && "bg-emerald-400/90",
                        item.tone === "amber" && (womensDay ? "bg-rose-400/90" : "bg-amber-400/90"),
                        item.tone !== "emerald" && item.tone !== "amber" && "bg-indigo-400/90"
                      )}
                    />
                    {item.label}
                  </div>
                )}
                {item.children
                  .filter((child) => child.featured)
                  .map((child) =>
                    collapsed ? (
                      <NavItem
                        key={child.label}
                        href={child.href}
                        icon={child.icon}
                        label={child.label}
                        active={!child.comingSoon && isActive(child.href)}
                        badge={child.badge}
                        comingSoon={child.comingSoon}
                        iconHighlight={child.iconHighlight}
                        tone={item.tone}
                        womensDay={womensDay}
                      />
                    ) : (
                      <FeaturedEventNavCard
                        key={child.label}
                        href={child.href}
                        label={child.label}
                        badge={child.badge}
                        hint={child.featuredHint}
                        imageSrc={child.featuredImage}
                        active={!child.comingSoon && isActive(child.href)}
                        onNavigate={() => {
                          if (typeof window !== "undefined" && window.innerWidth < 1280) {
                            setCollapsed(true);
                          }
                        }}
                      />
                    )
                  )}
                {item.children
                  .filter((child) => !child.featured)
                  .map((child) => (
                    <NavItem
                      key={child.label}
                      href={child.href}
                      icon={child.icon}
                      label={child.label}
                      active={!child.comingSoon && isActive(child.href)}
                      badge={child.badge}
                      comingSoon={child.comingSoon}
                      iconHighlight={child.iconHighlight}
                      tone={item.tone}
                      womensDay={womensDay}
                      nested
                    />
                  ))}
              </div>
            ) : (
              <NavItem
                key={item.label}
                href={item.href || "#"}
                icon={item.icon}
                label={item.label}
                active={item.href ? isActive(item.href) : false}
                tone={item.tone}
                womensDay={womensDay}
              />
            )
          )}
        </nav>
      </div>

      <div className="border-t py-4">
        <nav className="grid gap-1 px-2">
          <NavItem href="#" icon={<LogOut className="size-5" />} label="Đăng xuất" active={isActive("#")} womensDay={womensDay} />
        </nav>
      </div>
    </aside>
  );
}

type IconHighlight = "gold" | "crimson";

const ICON_HIGHLIGHT_STYLES: Record<
  IconHighlight,
  {
    collapsedActive: string;
    collapsedIdle: string;
    nestedActive: string;
    nestedIdle: string;
    bar: string;
    panelActive: string;
    iconBoxActive: string;
    iconBoxIdle: string;
    svg: string;
    collapsedSvg: string;
  }
> = {
  gold: {
    collapsedActive:
      "border-amber-400/80 bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-[0_0_16px_rgba(251,191,36,0.55)]",
    collapsedIdle:
      "border-amber-400/50 bg-amber-500/15 text-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.35)] hover:border-amber-300/70 hover:bg-amber-500/25",
    nestedActive: "text-white shadow-[0_6px_20px_rgba(251,191,36,0.4)]",
    nestedIdle: "text-amber-100 hover:bg-amber-500/10 hover:text-amber-50",
    bar: "bg-amber-400",
    panelActive: "border border-amber-300/70 bg-gradient-to-r from-amber-500/95 to-orange-500/95",
    iconBoxActive: "border-amber-200/50 bg-amber-300/20 shadow-[0_0_10px_rgba(251,191,36,0.45)]",
    iconBoxIdle:
      "border-amber-400/45 bg-amber-500/20 shadow-[0_0_10px_rgba(251,191,36,0.3)] group-hover:border-amber-300/60 group-hover:bg-amber-500/30",
    svg: "[&_svg]:text-amber-300 [&_svg]:drop-shadow-[0_0_4px_rgba(251,191,36,0.8)]",
    collapsedSvg: "[&_svg]:text-amber-300",
  },
  crimson: {
    collapsedActive:
      "border-rose-400/80 bg-gradient-to-br from-rose-600 to-red-700 text-white shadow-[0_0_16px_rgba(244,63,94,0.5)]",
    collapsedIdle:
      "border-rose-400/50 bg-rose-500/15 text-rose-300 shadow-[0_0_12px_rgba(244,63,94,0.32)] hover:border-rose-300/70 hover:bg-rose-500/25",
    nestedActive: "text-white shadow-[0_6px_20px_rgba(244,63,94,0.38)]",
    nestedIdle: "text-rose-100 hover:bg-rose-500/10 hover:text-rose-50",
    bar: "bg-rose-400",
    panelActive: "border border-rose-300/70 bg-gradient-to-r from-rose-600/95 to-red-600/95",
    iconBoxActive: "border-rose-200/50 bg-rose-300/20 shadow-[0_0_10px_rgba(244,63,94,0.42)]",
    iconBoxIdle:
      "border-rose-400/45 bg-rose-500/20 shadow-[0_0_10px_rgba(244,63,94,0.28)] group-hover:border-rose-300/60 group-hover:bg-rose-500/30",
    svg: "[&_svg]:text-rose-300 [&_svg]:drop-shadow-[0_0_4px_rgba(244,63,94,0.75)]",
    collapsedSvg: "[&_svg]:text-rose-300",
  },
};

function FeaturedEventNavCard({
  href,
  label,
  badge,
  hint,
  imageSrc,
  active,
  onNavigate,
}: {
  href: string;
  label: string;
  badge?: string;
  hint?: string;
  imageSrc?: string;
  active?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      className={cn(
        "group relative mb-1.5 block overflow-hidden rounded-xl border transition-all",
        active
          ? "border-amber-300/70 bg-gradient-to-br from-amber-500/25 via-orange-950/40 to-black/50 shadow-[0_8px_24px_rgba(245,158,11,0.22)]"
          : "border-amber-400/35 bg-gradient-to-br from-amber-950/55 via-[#1a0d08]/80 to-black/40 hover:border-amber-300/55 hover:shadow-[0_6px_18px_rgba(245,158,11,0.16)]"
      )}
    >
      {imageSrc ? (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[58%] opacity-50 transition-opacity group-hover:opacity-70">
          <Image src={imageSrc} alt="" fill className="object-cover object-[55%_20%]" sizes="140px" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#120806] via-[#120806]/88 to-transparent" />
        </div>
      ) : null}

      <div className="relative z-10 flex items-start gap-2.5 px-2.5 py-3">
        <span
          className={cn(
            "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
            active
              ? "border-amber-200/50 bg-amber-400/25 text-amber-50"
              : "border-amber-400/40 bg-black/35 text-amber-200"
          )}
        >
          <Crown className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="mb-1 flex flex-wrap items-center gap-1.5">
            {badge ? (
              <span className="inline-flex items-center gap-1 rounded border border-amber-300/50 bg-amber-400/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-amber-100">
                <Zap className="h-2.5 w-2.5" />
                {badge}
              </span>
            ) : null}
            {active ? (
              <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300/90">Đang mở</span>
            ) : null}
          </span>
          <span className="block text-[13px] font-bold leading-snug text-amber-50">{label}</span>
          {hint ? <span className="mt-0.5 block text-[10px] font-medium text-amber-100/55">{hint}</span> : null}
        </span>
      </div>
    </Link>
  );
}

interface NavItemProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  tone?: "indigo" | "emerald" | "amber";
  active?: boolean;
  nested?: boolean;
  badge?: string;
  comingSoon?: boolean;
  iconHighlight?: IconHighlight;
  womensDay?: boolean;
}

function NavItem({
  href,
  icon,
  label,
  tone = "indigo",
  active,
  nested = false,
  badge,
  comingSoon = false,
  iconHighlight,
  womensDay = false,
}: NavItemProps) {
  const { collapsed, setCollapsed } = useSidebar();
  const { width } = useWindowSize();
  const isExternal = href.startsWith("http://") || href.startsWith("https://");
  const amberAsRose = womensDay && tone === "amber";

  const handleClick = () => {
    if (width < 1280) setCollapsed(true);
  };

  const linkProps = isExternal
    ? { href, target: "_blank", rel: "noopener noreferrer" as const }
    : { href };
  const highlight = iconHighlight ? ICON_HIGHLIGHT_STYLES[iconHighlight] : null;

  if (collapsed) {
    return (
      <div className="relative group flex items-center justify-center">
        <Link
          {...linkProps}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg border transition-all",
            active
              ? highlight
                ? highlight.collapsedActive
                : amberAsRose
                  ? "border-rose-400/70 bg-gradient-to-br from-rose-500 to-pink-500 text-white shadow-[0_0_14px_rgba(244,63,94,0.45)]"
                  : "border-indigo-400/70 bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-[0_0_14px_rgba(99,102,241,0.45)]"
              : highlight
                ? highlight.collapsedIdle
                : "border-transparent hover:border-border hover:bg-accent hover:text-accent-foreground",
            nested && "h-8 w-8"
          )}
        >
          <span className={cn("[&_svg]:h-4 [&_svg]:w-4", highlight?.collapsedSvg)}>
            {icon}
          </span>
        </Link>
        {badge && (
          <span className="absolute -top-0.5 -right-0.5 h-[3px] w-[3px] rounded-full bg-indigo-500 shadow-[0_0_4px_rgba(99,102,241,0.9)]" />
        )}
      </div>
    );
  }

  if (nested) {
    return (
      <Link
        {...linkProps}
        onClick={handleClick}
        className={cn(
          "group relative flex min-w-0 items-center gap-2.5 rounded-lg py-2.5 pl-2.5 pr-2 text-sm font-medium leading-6 transition-all",
          comingSoon
            ? "text-slate-500 hover:bg-white/5 hover:text-slate-400"
            : active
              ? highlight
                ? highlight.nestedActive
                : tone === "emerald"
                  ? "text-white shadow-[0_6px_20px_rgba(16,185,129,0.35)]"
                  : amberAsRose
                    ? "text-white shadow-[0_6px_20px_rgba(244,63,94,0.35)]"
                    : tone === "amber"
                      ? "text-white shadow-[0_6px_20px_rgba(245,158,11,0.35)]"
                      : "text-white shadow-[0_6px_20px_rgba(99,102,241,0.45)]"
              : highlight
                ? highlight.nestedIdle
                : "text-slate-300 hover:bg-white/10 hover:text-white"
        )}
      >
        <span
          className={cn(
            "absolute inset-y-1 left-0 w-1 rounded-full transition-opacity",
            active && !comingSoon ? "opacity-100" : "opacity-0 group-hover:opacity-40",
            highlight
              ? highlight.bar
              : tone === "emerald"
                ? "bg-emerald-400"
                : amberAsRose
                  ? "bg-rose-400"
                  : tone === "amber"
                    ? "bg-amber-400"
                    : "bg-indigo-400"
          )}
        />
        <span
          className={cn(
            "absolute inset-0 rounded-lg",
            active &&
              !comingSoon &&
              (highlight
                ? highlight.panelActive
                : tone === "emerald"
                  ? "border border-emerald-300/70 bg-gradient-to-r from-emerald-500/95 to-teal-500/95"
                  : amberAsRose
                    ? "border border-rose-300/70 bg-gradient-to-r from-rose-500/95 to-pink-500/95"
                    : tone === "amber"
                      ? "border border-amber-300/70 bg-gradient-to-r from-amber-500/95 to-orange-500/95"
                      : "border border-indigo-300/70 bg-gradient-to-r from-indigo-500/95 to-violet-500/95")
          )}
        />
        <span
          className={cn(
            "relative z-10 shrink-0 rounded-md border p-1",
            comingSoon
              ? "border-white/5 bg-white/[0.03]"
              : highlight
                ? active
                  ? highlight.iconBoxActive
                  : highlight.iconBoxIdle
                : active
                  ? "border-white/30 bg-white/15"
                  : "border-white/10 bg-white/5 group-hover:border-white/30 group-hover:bg-white/15"
          )}
        >
          <span className={cn("[&_svg]:h-4 [&_svg]:w-4", comingSoon && "[&_svg]:text-slate-500", highlight?.svg)}>
            {icon}
          </span>
        </span>
        <span className="relative z-10 min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className={cn("truncate", comingSoon && "text-slate-500")}>{label}</span>
            {badge && (
              <span
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-px text-[9px] font-semibold leading-tight",
                  comingSoon
                    ? "border-slate-600/50 bg-slate-800/60 text-slate-400"
                    : active
                      ? "border-white/40 bg-white/15 text-white"
                      : "border-amber-400/60 bg-amber-500/20 text-amber-200"
                )}
              >
                {badge.toLowerCase() === "hot" ? <span className="text-[10px] leading-none">🔥</span> : null}
                {badge}
              </span>
            )}
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      {...linkProps}
      onClick={handleClick}
      className={cn(
        "group relative flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-medium leading-6 transition-all",
        active
          ? "text-white shadow-[0_6px_18px_rgba(99,102,241,0.35)]"
          : "border-transparent text-slate-300 hover:border-white/20 hover:bg-white/10 hover:text-white"
      )}
    >
      <span
        className={cn(
          "absolute inset-y-1 left-0 w-1 rounded-full transition-opacity",
          active ? "opacity-100" : "opacity-0 group-hover:opacity-40",
          tone === "emerald" && "bg-emerald-400",
          amberAsRose && "bg-rose-400",
          tone === "amber" && !amberAsRose && "bg-amber-400",
          tone === "indigo" && "bg-indigo-400"
        )}
      />
      <span
        className={cn(
          "absolute inset-0 rounded-lg",
          active && tone === "emerald" && "border border-emerald-400/70 bg-gradient-to-r from-emerald-500 to-teal-500",
          active && amberAsRose && "border border-rose-400/70 bg-gradient-to-r from-rose-500 to-pink-500",
          active && tone === "amber" && !amberAsRose && "border border-amber-400/70 bg-gradient-to-r from-amber-500 to-orange-500",
          active && tone === "indigo" && "border border-indigo-400/70 bg-gradient-to-r from-indigo-500 to-violet-500"
        )}
      />
      <span
        className={cn(
          "relative z-10 shrink-0 rounded-md border p-1",
          active
            ? "border-white/30 bg-white/15"
            : "border-white/10 bg-white/5 group-hover:border-white/30 group-hover:bg-white/15"
        )}
      >
        <span className="[&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      </span>
      <span className="relative z-10 min-w-0 truncate">{label}</span>
      {badge ? (
        <span className="relative z-10 ml-auto shrink-0 rounded-full border border-amber-400/60 bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-amber-200">
          {badge}
        </span>
      ) : null}
    </Link>
  );
}
